package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.DriveHealthEntry
import com.dotdrives.repository.PipelineHealthRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class PipelineHealthUiState(
    val loading: Boolean = false,
    val entries: List<DriveHealthEntry> = emptyList(),
    val error: String? = null,
)

class PipelineHealthController(
    private val repo: PipelineHealthRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<PipelineHealthUiState>(PipelineHealthUiState(), scope) {
    private var generation = 0L

    fun load() {
        val current = nextGeneration()
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                repo.list(orgId)
                    .onSuccess { entries ->
                        if (!isCurrent(current)) return@onSuccess
                        update { it.copy(loading = false, entries = entries, error = null) }
                    }
                    .onFailure { publishError(it, current) }
            } catch (error: Exception) {
                publishError(error, current)
            }
        }
    }

    fun retry() = load()

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        // A transient failure must surface as error+retry, never wipe `entries` back to
        // empty -- that combination reads as "nothing is stuck," which is not what happened.
        update { it.copy(loading = false, error = error.message ?: "Something went wrong") }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(candidate: Long): Boolean = candidate == generation
}
